import { useQuery } from "@apollo/client/react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { z } from "zod";
import { PageLoader } from "../../components/PageLoader.tsx";
import { RefreshProvider } from "../../context/RefreshContext.tsx";
import { GetRecipeByVersionDocument, VersionStatus } from "../../gql/generated/graphqlSchema.tsx";
import { useSafeParams } from "../../hooks/useSafeParams.ts";
import { routes } from "../../routes.ts";
import { RecipeProvider } from "./RecipeContext.tsx";
import { RecipeDetailView } from "./view/RecipeDetailView.tsx";

const paramsSchema = z.object({
  id: z.string().min(1),
  version: z.coerce.number().int().positive(),
});

const noop = () => {};

const RecipeDetailVersionPageInner = () => {
  const { t } = useTranslation();
  const { id, version } = useSafeParams(paramsSchema);
  const { data, loading, refetch } = useQuery(GetRecipeByVersionDocument, {
    variables: { id, versionNumber: version },
  });

  if (loading) {
    return <PageLoader />;
  }

  const recipe = data?.recipeByVersion;
  if (!recipe) {
    return <Typography.Text type="secondary">{t("recipes.notFound")}</Typography.Text>;
  }

  if (recipe.versionStatus !== VersionStatus.Archived) {
    return <Navigate replace to={routes.recipeDetail.to(id)} />;
  }

  return (
    <RecipeProvider readOnly recipe={recipe} refetch={() => refetch()} setRecipe={noop}>
      <RecipeDetailView key={recipe.versionId} />
    </RecipeProvider>
  );
};

// eslint-disable-next-line react/no-multi-comp
export const RecipeDetailVersionPage = () => (
  <RefreshProvider>
    <RecipeDetailVersionPageInner />
  </RefreshProvider>
);
